import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { Mic, MicOff, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useLiveSpeechDictation } from '@/hooks/useLiveSpeechDictation';
import { useAudioDictation, type AudioDictationStatus } from '@/hooks/useAudioDictation';
import { TEMPLATES, type TemplateType, normalizeTemplateType } from '@/lib/templates';
import { toast } from 'sonner';

interface IdeaInputProps {
  onSubmit: (text: string, templateType: TemplateType) => Promise<void>;
  disabled?: boolean;
}

const placeholders = [
  'An idea you don\'t want to lose…',
  'Something that just crossed your mind…',
  'A thought worth keeping…',
  'Say it before it fades…',
];

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const WHISPER_STATUS_LABELS: Record<AudioDictationStatus, string> = {
  idle: '',
  recording: 'Recording…',
  uploading: 'Uploading…',
  transcribing: 'Transcribing…',
  error: 'Error',
};

export const IdeaInput = forwardRef<HTMLTextAreaElement, IdeaInputProps>(
  ({ onSubmit, disabled }, ref) => {
    const [value, setValue] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('base');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [placeholder] = useState(
      () => placeholders[Math.floor(Math.random() * placeholders.length)]
    );

    const baseTextRef = useRef('');

    // ── Template selection (metadata only, no pre-fill) ──
    const handleTemplateSelect = (type: TemplateType) => {
      setSelectedTemplate(type);
    };

    // ── Live Speech (primary) ──
    const live = useLiveSpeechDictation();

    // ── Whisper fallback ──
    const handleWhisperText = useCallback((text: string) => {
      setValue((prev) => {
        if (!prev.trim()) return text;
        const needsSpace = !prev.endsWith('\n') && !prev.endsWith(' ');
        return prev + (needsSpace ? ' ' : '') + text;
      });
    }, []);

    const whisper = useAudioDictation({ onText: handleWhisperText });
    const useLiveMode = live.isSupported;

    // ── Sync live text ──
    useEffect(() => {
      if (live.isListening && live.liveText) {
        const base = baseTextRef.current;
        const needsSpace = base.length > 0 && !base.endsWith('\n') && !base.endsWith(' ');
        setValue(base + (needsSpace ? ' ' : '') + live.liveText);
      }
    }, [live.isListening, live.liveText]);

    // ── Error toasts ──
    const lastErrorRef = useRef<string | null>(null);
    useEffect(() => {
      const err = useLiveMode ? live.error : whisper.error;
      const hasError = useLiveMode ? !!live.error : whisper.status === 'error';
      if (err && hasError && err !== lastErrorRef.current) {
        lastErrorRef.current = err;
        toast.error(err);
      }
      if (!hasError) lastErrorRef.current = null;
    }, [useLiveMode, live.error, whisper.error, whisper.status]);

    const isListeningLive = useLiveMode && live.isListening;
    const isWhisperRecording = !useLiveMode && whisper.status === 'recording';
    const isWhisperBusy = !useLiveMode && (whisper.status === 'uploading' || whisper.status === 'transcribing');
    const isActive = isListeningLive || isWhisperRecording || isWhisperBusy;

    const handleMicClick = () => {
      if (disabled || isSubmitting) return;
      if (useLiveMode) {
        if (live.isListening) {
          const committed = live.stop();
          const base = baseTextRef.current;
          const needsSpace = base.length > 0 && !base.endsWith('\n') && !base.endsWith(' ');
          setValue(base + (committed ? (needsSpace ? ' ' : '') + committed : ''));
        } else {
          baseTextRef.current = value;
          live.start(value);
        }
      } else {
        toast.error("Dictation not available in this browser. Try typing.");
      }
    };

    const handlePointerDown = () => {
      if (disabled || isSubmitting || useLiveMode) return;
      if (!whisper.isSupported) { toast.error("Dictation not available. Try typing."); return; }
      if (whisper.status === 'recording') return;
      whisper.startRecording();
    };

    const handlePointerUp = () => {
      if (!useLiveMode && whisper.status === 'recording') whisper.stopRecording();
    };

    const handleCancel = () => {
      if (useLiveMode) { live.stop(); setValue(baseTextRef.current); }
      else whisper.cancel();
    };

    const handleSubmit = async () => {
      if (!value.trim() || isSubmitting || disabled) return;
      if (isActive) { if (useLiveMode) live.stop(); else whisper.cancel(); }
      setIsSubmitting(true);
      try {
        await onSubmit(value.trim(), selectedTemplate);
        setValue('');
        setSelectedTemplate('base');
        baseTextRef.current = '';
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    };

    let statusLabel = '';
    if (isListeningLive) statusLabel = 'Listening…';
    else if (isWhisperRecording || isWhisperBusy) statusLabel = WHISPER_STATUS_LABELS[whisper.status];

    const showPulse = isListeningLive || isWhisperRecording;
    const showCancel = isListeningLive || isWhisperBusy;

    return (
      <div className="w-full max-w-xl mx-auto">
        {/* Template pills */}
        <div className="flex flex-wrap gap-1.5 mb-3 justify-center">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => handleTemplateSelect(t.key)}
              disabled={disabled || isSubmitting}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                'border',
                selectedTemplate === t.key
                  ? 'bg-primary/10 border-primary/30 text-primary'
                  : 'bg-muted/30 border-border/40 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )}
              title={t.description}
            >
              {t.label}
            </button>
          ))}
        </div>
        {selectedTemplate !== 'base' && (
          <p className="text-[11px] text-muted-foreground/50 text-center mb-1 font-light">
            Structure will be generated automatically after planting.
          </p>
        )}

        <div className="relative">
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => { if (!live.isListening) setValue(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            readOnly={live.isListening}
            className={cn(
              'resize-none pr-12',
              'min-h-[120px]',
              'bg-card/80 backdrop-blur-sm',
              'border border-secondary/40 rounded-2xl',
              'text-base leading-relaxed',
              'placeholder:text-muted-foreground/40 placeholder:font-light',
              'focus:border-secondary/60 focus:ring-0 focus:ring-offset-0',
              'focus:shadow-[inset_0_1px_12px_0_hsl(var(--secondary)/0.08)]',
              'transition-all duration-300 ease-out',
              showPulse && 'border-primary/50 shadow-[inset_0_1px_12px_0_hsl(var(--primary)/0.06)]'
            )}
          />

          <div className="absolute bottom-3 right-3">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={useLiveMode ? handleMicClick : undefined}
              onPointerDown={!useLiveMode ? handlePointerDown : undefined}
              onPointerUp={!useLiveMode ? handlePointerUp : undefined}
              onPointerCancel={!useLiveMode ? handlePointerUp : undefined}
              onPointerLeave={!useLiveMode && isWhisperRecording ? handlePointerUp : undefined}
              disabled={disabled || isSubmitting || isWhisperBusy}
              className={cn(
                'h-7 w-7 transition-colors',
                !useLiveMode && 'touch-none select-none',
                (isListeningLive || isWhisperRecording)
                  ? 'text-primary hover:text-primary/80'
                  : 'text-muted-foreground/50 hover:text-muted-foreground'
              )}
              title={useLiveMode ? (live.isListening ? 'Stop dictation' : 'Start dictation') : (isWhisperRecording ? 'Release to stop' : 'Hold to talk')}
            >
              {isWhisperBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isListeningLive || isWhisperRecording) ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Button>
          </div>

          {showPulse && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
            </div>
          )}
        </div>

        {isActive && (
          <div className="mt-2 flex items-center justify-between px-1">
            <div className="flex items-center gap-1.5">
              <span className={cn('text-xs font-medium', (isListeningLive || isWhisperRecording) ? 'text-primary' : 'text-muted-foreground')}>
                {statusLabel}
              </span>
              {isWhisperRecording && <span className="text-xs text-muted-foreground/50">{formatTime(whisper.elapsedSeconds)}</span>}
            </div>
            {showCancel && (
              <Button type="button" size="sm" variant="ghost" onClick={handleCancel} className="h-6 px-2 text-xs text-muted-foreground">
                <X className="h-3 w-3 mr-1" />Cancel
              </Button>
            )}
          </div>
        )}

        <div className={cn('flex flex-col items-center gap-2', isActive ? 'mt-3' : 'mt-6')}>
          <Button
            onClick={handleSubmit}
            disabled={!value.trim() || isSubmitting || disabled}
            variant="secondary"
            className={cn(
              'px-6 py-2 h-auto rounded-full text-sm font-normal',
              'bg-primary/80 hover:bg-primary/90 text-primary-foreground',
              'disabled:opacity-40 transition-all duration-200'
            )}
          >
            {isSubmitting ? (<><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Planting…</>) : 'Plant Seed'}
          </Button>
          <span className="text-[11px] text-muted-foreground/40 font-light">
            You can refine it later.
          </span>
        </div>
      </div>
    );
  }
);

IdeaInput.displayName = 'IdeaInput';
